import type {
  AuthResponse,
  BalanceResponse,
  CashoutResponse,
  PressResponse,
  RoundStartRequest,
  RoundStartResponse,
  VerifyResponse,
} from "@cslame/shared";

const TOKEN_KEY = "cslame.token";

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function setToken(t: string | null) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    "content-type": "application/json",
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (init.auth !== false) {
    const t = getToken();
    if (t) headers.authorization = `Bearer ${t}`;
  }
  const res = await fetch(`/api${path}`, { ...init, headers });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = new Error(body?.error || `HTTP ${res.status}`) as Error & {
      status: number;
      body: unknown;
    };
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body as T;
}

export const api = {
  async auth(initData: string): Promise<AuthResponse> {
    const res = await request<AuthResponse>("/auth", {
      method: "POST",
      body: JSON.stringify({ initData }),
      auth: false,
    });
    setToken(res.token);
    return res;
  },

  balance: () => request<BalanceResponse>("/balance"),

  activeRound: () =>
    request<{
      round:
        | {
            id: string;
            stake: number;
            difficulty: number;
            seedHash: string;
            picksCount: number;
            revealed: string;
            multiplier: number;
            status: "active";
          }
        | null;
    }>("/round/active"),

  startRound: (body: RoundStartRequest) =>
    request<RoundStartResponse>("/round/start", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  press: (roundId: string, digit: number) =>
    request<PressResponse>(`/round/${roundId}/press`, {
      method: "POST",
      body: JSON.stringify({ digit }),
    }),

  cashout: (roundId: string) =>
    request<CashoutResponse>(`/round/${roundId}/cashout`, { method: "POST" }),

  verify: (roundId: string) =>
    request<VerifyResponse>(`/round/${roundId}/verify`),

  clearToken: () => setToken(null),
};
